/**
 * Inspector API Service
 * 
 * Handles all API calls for the inspector module
 */

import axios, { AxiosInstance } from 'axios'
import { ExceptionObjectType, generateExceptionUrl } from '../../utils/urls'
import type { ExceptionDrawerDetailBlock } from '../../config/exceptions/types'
import type { ReferenceLink } from '../../components/shared/referenceLinks/types'

export interface ExceptionTagDetailResponse {
  detail_blocks: ExceptionDrawerDetailBlock[]
  links: ReferenceLink[]
}

/**
 * Params every per-domain exception list wrapper accepts. Domain extras
 * extend this instead of re-declaring the base set per method.
 */
export interface ExceptionListParams {
  limit?: number
  offset?: number
  search?: string | null
  status?: string | null
  since_days?: number | null
}

/** Product-domain extras (stock-management / product-setup / product-pricing). */
export interface ProductExceptionListParams extends ExceptionListParams {
  main_category?: string | string[] | null
  product_type?: string | string[] | null
  active?: string | null
  has_stock?: boolean | null
}

/** Sales extras - compare mismatch axis + SK-LS tag-only filters. */
export interface SalesExceptionListParams extends ExceptionListParams {
  mismatch?: string | string[] | null
  issue_code?: string | null
  owner?: string | null
}

/** Envelope every Data Explorer browse list returns. */
export interface DataExplorerListResponse {
  items: Record<string, any>[]
  total: number
  limit: number
  offset: number
  /** Non-DNU outlets (sales + products) - feeds filter options / columns. */
  outlets?: Array<{ id: string; name: string }>
}

/** ``/{fp}/data/products/facets`` response. */
export interface DataExplorerProductFacets {
  main_categories: Array<{ value: string | null; count: number }>
}

/** Tuple identity for the central drawer-detail endpoint. */
export interface ExceptionDrawerTuple {
  data_domain: string
  exception_code: string
  object_id: string
}

/** Full drawer payload for one exception tuple (see backend drawer.py). */
export interface ExceptionDrawerDetailResponse {
  // Fully-stamped per-domain row: subtitle source fields, exception_status,
  // exception_tag_id, errors[], recently_closed_errors[], detail_blocks[].
  row: Record<string, any>
  exception_status: string | null
  exception_tag_id: number | null
  exception_type: string | null
  allow_ignore: boolean
  name_short: string | null
}

class InspectorApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: '/api/inspector',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      // Repeat array params (`mismatch=a&mismatch=b`) instead of the
      // axios default `mismatch[]=a&mismatch[]=b`, so FastAPI's
      // `list[str]` Query parses them natively.
      paramsSerializer: { indexes: null },
    })
  }

  /**
   * Get exception type metadata
   */
  async getExceptionType(exceptionCode: string) {
    const response = await this.client.get(`/exception-types/${exceptionCode}`)
    return response.data
  }

  /**
   * Get exception tags for a franchise and exception type
   */
  async getExceptionTags(
    fpCode: string,
    exceptionCode: string,
    params?: {
      limit?: number
      offset?: number
    }
  ) {
    const response = await this.client.get(
      `/exception-tags/${fpCode}/${exceptionCode}`,
      {
        params: params || { limit: 100, offset: 0 }
      }
    )
    return response.data
  }

  /**
   * Fetch ``/{prefix}/exceptions/types/{fpCode}`` and attach inspector
   * drill-down URLs. Used directly by callers that already know the
   * domain prefix (e.g. the sibling-group switcher in
   * DynamicExceptionRoute); the per-domain ``getXExceptionTypes``
   * wrappers below are kept for existing call sites that read better
   * with a named method.
   */
  async getExceptionTypes(
    prefix: ExceptionObjectType,
    fpCode: string,
  ) {
    const response = await this.client.get(
      `/${prefix}/exceptions/types/${fpCode}`,
    )
    if (response.data?.exception_types) {
      response.data.exception_types = response.data.exception_types.map((exception: any) => ({
        ...exception,
        url: generateExceptionUrl(fpCode, prefix, exception.code),
      }))
    }
    return response.data
  }

  /**
   * Internal helper: fetch ``/{prefix}/exceptions/{code}/{fpCode}``.
   *
   * ``mismatch`` is honoured only by the sales endpoint; other domains
   * ignore it. ``issue_code`` / ``owner`` / ``status`` / ``since_days`` are
   * SK-LS-only filters honoured by the sales endpoint's tag-only branch
   * (see ``ex-sale-sk-ls-invoice-sync-failure`` dispatch in
   * ``backend/api/routes/inspector/exceptions/sales.py``). See
   * ``SALE_MISMATCH_KEYS`` in ``shared/sale_alignment.py`` for mismatch
   * values.
   */
  async _fetchExceptions(
    prefix: ExceptionObjectType,
    fpCode: string,
    exceptionCode: string,
    params?: SalesExceptionListParams &
      ProductExceptionListParams & { object_id?: string | null },
  ) {
    // Default to a generous page so legacy callers that paginate
    // client-side still get the full set in one shot. Callers that own
    // server-side pagination (DynamicExceptionRoute on the sales-compare
    // base code or the SK-LS code) supply their own limit/offset and
    // bypass this default.
    const merged: Record<string, any> = {
      limit: 1000,
      offset: 0,
      ...(params || {}),
    }
    for (const key of [
      'mismatch',
      'search',
      'issue_code',
      'owner',
      'status',
      'since_days',
      'object_id',
    ]) {
      const v = merged[key]
      if (v == null || v === '') delete merged[key]
      else if (Array.isArray(v) && v.length === 0) delete merged[key]
    }
    const response = await this.client.get(
      `/${prefix}/exceptions/${exceptionCode}/${fpCode}`,
      { params: merged },
    )
    return response.data
  }

  getSalesExceptionTypes(fpCode: string) {
    return this.getExceptionTypes('sales', fpCode)
  }

  getSalesExceptions(
    fpCode: string,
    exceptionCode: string,
    params?: SalesExceptionListParams,
  ) {
    return this._fetchExceptions('sales', fpCode, exceptionCode, params)
  }

  /**
   * Per-mismatch counts for the sales-compare chip strip. Returns
   * ``{ counts: { all, date, client, status, statusVoided, lineItems,
   * amounts, amountsExTax, amountsTax, amountsIncTax, payments } }``.
   */
  async getSalesMismatchCounts(
    fpCode: string,
    params?: { exception_code?: string },
  ): Promise<{ counts: Record<string, number> }> {
    const response = await this.client.get(
      `/sales/exceptions/mismatch-counts/${fpCode}`,
      { params: params || {} },
    )
    return response.data
  }

  /**
   * Lazy "Why + How" detail for one exception tag. Feeds the drawer's
   * linked-sibling expand and the Admin drawer (phase 3b) from a single
   * read-only endpoint keyed by ``tag_id``.
   */
  async getExceptionTagDetail(
    fpCode: string,
    tagId: number,
  ): Promise<ExceptionTagDetailResponse> {
    const response = await this.client.get(
      `/exception-tags/${tagId}/detail/${fpCode}`,
    )
    return response.data
  }

  /**
   * Full drawer payload for an exception tuple
   * (``fp_code + data_domain + exception_code + object_id``).
   *
   * The single resolver both the admin DQ Workbench drawer and the inspector
   * deep-link mount feed ``ExceptionDrawer``, so they render the same row +
   * status + linked siblings. ``object_id`` is the last (``:path``) segment, so
   * it is URL-encoded to survive ids that contain reserved characters.
   */
  async getExceptionDrawerDetail(
    fpCode: string,
    tuple: ExceptionDrawerTuple,
  ): Promise<ExceptionDrawerDetailResponse> {
    const { data_domain, exception_code, object_id } = tuple
    const response = await this.client.get(
      `/exceptions/${fpCode}/drawer/${data_domain}/${exception_code}/${encodeURIComponent(object_id)}`,
    )
    return response.data
  }

  getStockManagementExceptionTypes(fpCode: string) {
    return this.getExceptionTypes('stock-management', fpCode)
  }

  getStockManagementExceptions(
    fpCode: string,
    exceptionCode: string,
    params?: ProductExceptionListParams,
  ) {
    return this._fetchExceptions('stock-management', fpCode, exceptionCode, params)
  }

  getProductSetupExceptionTypes(fpCode: string) {
    return this.getExceptionTypes('product-setup', fpCode)
  }

  getProductSetupExceptions(
    fpCode: string,
    exceptionCode: string,
    params?: ProductExceptionListParams,
  ) {
    return this._fetchExceptions('product-setup', fpCode, exceptionCode, params)
  }

  getProductPricingExceptionTypes(fpCode: string) {
    return this.getExceptionTypes('product-pricing', fpCode)
  }

  getProductPricingExceptions(
    fpCode: string,
    exceptionCode: string,
    params?: ProductExceptionListParams,
  ) {
    return this._fetchExceptions('product-pricing', fpCode, exceptionCode, params)
  }

  getSystemsExceptionTypes(fpCode: string) {
    return this.getExceptionTypes('systems', fpCode)
  }

  getSystemExceptions(
    fpCode: string,
    exceptionCode: string,
    params?: ExceptionListParams,
  ) {
    return this._fetchExceptions('systems', fpCode, exceptionCode, params)
  }

  getClientExceptionTypes(fpCode: string) {
    return this.getExceptionTypes('client', fpCode)
  }

  getClientExceptions(
    fpCode: string,
    exceptionCode: string,
    params?: ExceptionListParams,
  ) {
    return this._fetchExceptions('client', fpCode, exceptionCode, params)
  }

  // --- Data Explorer browse endpoints ------------------------------------

  /**
   * Internal helper for the Data Explorer browse lists
   * (``/{fpCode}/data/{view}``). Strips empty params so URLs stay
   * minimal; array params repeat via the client's paramsSerializer.
   */
  async _fetchDataExplorer(
    view: 'sales' | 'products' | 'clients',
    fpCode: string,
    params?: Record<string, any>,
  ): Promise<DataExplorerListResponse> {
    const merged: Record<string, any> = { ...(params || {}) }
    for (const key of Object.keys(merged)) {
      const v = merged[key]
      if (v == null || v === '') delete merged[key]
      else if (Array.isArray(v) && v.length === 0) delete merged[key]
    }
    const response = await this.client.get(`/${fpCode}/data/${view}`, {
      params: merged,
    })
    return response.data
  }

  getDataExplorerSales(fpCode: string, params?: Record<string, any>) {
    return this._fetchDataExplorer('sales', fpCode, params)
  }

  getDataExplorerProducts(fpCode: string, params?: Record<string, any>) {
    return this._fetchDataExplorer('products', fpCode, params)
  }

  getDataExplorerClients(fpCode: string, params?: Record<string, any>) {
    return this._fetchDataExplorer('clients', fpCode, params)
  }

  /** Whole-catalogue Main Category counts for the products filter. */
  async getDataExplorerProductFacets(
    fpCode: string,
  ): Promise<DataExplorerProductFacets> {
    const response = await this.client.get(`/${fpCode}/data/products/facets`)
    return response.data
  }

  getAccountingExceptionTypes(fpCode: string) {
    return this.getExceptionTypes('accounting', fpCode)
  }

  getAccountingExceptions(
    fpCode: string,
    exceptionCode: string,
    params?: ExceptionListParams,
  ) {
    return this._fetchExceptions('accounting', fpCode, exceptionCode, params)
  }

  /**
   * Compare a sale between Lightspeed and Xero (line items + totals).
   */
  async getCompareSales(
    fpCode: string,
    saleId: string,
    xeroInvoiceId?: string,
  ): Promise<CompareSalesResponse> {
    const params = xeroInvoiceId ? { xero_invoice_id: xeroInvoiceId } : {}
    const response = await this.client.get(
      `/sales/${saleId}/compare/${fpCode}`,
      { params }
    )
    return response.data
  }

  /**
   * Get full per-member detail for a single Potential Duplicates group.
   * Powers the expandable comparison view in the product-setup exception
   * list and the Duplicates tab inside Product Catalogue Review.
   */
  async getDuplicateGroupDetail(
    fpCode: string,
    groupId: string,
  ): Promise<DuplicateGroupDetailResponse> {
    const response = await this.client.get(
      `/product-setup/exceptions/ex-product-ls-duplicate/${fpCode}/group/${encodeURIComponent(groupId)}`,
    )
    return response.data
  }

  /**
   * Grouped duplicate-sales list - one row per detector group, with a
   * compact `members` preview array (envelope key: `inventory`). The
   * canonical list for `ex-sale-ls-duplicate`; wired into the exception
   * route via `duplicateSaleConfig.fetchOverride` and used directly by the
   * Duplicate Sales tool page.
   */
  async getDuplicateSaleGroups(
    fpCode: string,
    params?: Record<string, any>,
  ): Promise<Record<string, any>> {
    const response = await this.client.get(
      `/sales/tools/duplicate-sales/${fpCode}`,
      { params: { limit: 500, ...(params ?? {}) } },
    )
    return response.data
  }

  /**
   * Get full per-member detail for a single duplicate-sale group.
   * Powers the expandable comparison view in the "Duplicate Sales"
   * inspector tool.
   */
  async getDuplicateSaleGroupDetail(
    fpCode: string,
    groupId: string,
  ): Promise<DuplicateSaleGroupDetailResponse> {
    const response = await this.client.get(
      `/sales/exceptions/ex-sale-ls-duplicate/${fpCode}/group/${encodeURIComponent(groupId)}`,
    )
    return response.data
  }
}

// Type definitions for line item details
export interface LineItemLS {
  id: string
  product_name: string
  quantity: number
  total_price: number
  amount_ex_tax: number
  tax: number
  amount_inc_tax: number
  tax_label?: string | null
  tax_rate?: number | null
  note?: string
  sequence?: number
  is_synthetic_note?: boolean
  is_credit_workaround?: boolean
}

export interface LineItemXero {
  id: string
  description: string
  quantity: number
  line_amount: number
  amount_ex_tax: number
  amount_inc_tax: number
  tax: number
  tax_label?: string | null
  tax_rate?: number | null
  is_synthetic_note?: boolean
  is_credit_workaround?: boolean
}

export interface LSCreditWorkaroundLine {
  description: string
  amount: number
  quantity: number
}

export interface XeroCreditAllocation {
  date?: string | null
  amount: number
  credit_note_id: string
  credit_note_number?: string | null
  credit_note_type?: string | null
  link?: string | null
}

export interface LineItemPair {
  ls_index: number
  xero_index: number
  qty_match: boolean
  price_match: boolean
  description_match: boolean
}

export interface CompareSalesResponse {
  sale_ls_id: string
  xero_invoice_id: string | null
  fp_name?: string
  // Header metadata for deep-link cold loads - lets the dialog reconstruct the
  // title bar (LS ref + open-in-LS) and system-panel links without the
  // originating row. The dialog reads `target.X ?? viewData.X`.
  invoice_number?: string | null
  xero_reference?: string | null
  xero_invoice_number?: string | null
  lightspeed_sale_link?: string | null
  xero_invoice_link?: string | null
  lightspeed: {
    count: number
    total_ex_tax: number
    total_inc_tax: number
    total_tax: number
    payments_total?: number | null
    payment_count?: number | null
    payments?: Array<{
      date?: string | null
      amount: number
      method?: string | null
    }>
    credit_workaround_lines?: LSCreditWorkaroundLine[]
    credit_workaround_total?: number | null
    credit_workaround_count?: number | null
    status?: string | null
    ls_created_at?: string | null
    ls_updated_at?: string | null
    sale_date?: string | null
    customer_name?: string | null
    items: LineItemLS[]
  }
  xero: {
    count: number
    kind?: 'invoice' | 'credit_note' | 'missing'
    total_ex_tax: number
    total_inc_tax: number
    total_tax: number
    amount_paid?: number | null
    amount_credited?: number | null
    settled_total?: number | null
    payment_count?: number | null
    payments?: Array<{
      date?: string | null
      amount: number
      reference?: string | null
      account_code?: string | null
      xero_payment_id?: string | null
      link?: string | null
    }>
    credit_allocations?: XeroCreditAllocation[]
    credit_count?: number | null
    status?: string | null
    created_at?: string | null
    updated_at?: string | null
    xero_updated_date_utc?: string | null
    invoice_date?: string | null
    contact_name?: string | null
    items: LineItemXero[]
  }
  comparison: {
    matched_count: number
    error_count?: number
    warning_count?: number
    pairs: LineItemPair[]
    unmatched_ls_indices: number[]
    unmatched_xero_indices: number[]
    unmatched_ls_warnings?: number[]  // Indices of $0/rounding items (warnings, not errors)
    unmatched_xero_warnings?: number[]
  }
}

export interface DuplicateGroupMember {
  ls_id: string
  sku?: string | null
  name: string
  variant_name?: string | null
  handle?: string | null
  supplier_id?: string | null
  supplier_code?: string | null
  supplier_name?: string | null
  brand_id?: string | null
  category_name?: string | null
  price_including_tax?: number | null
  price_excluding_tax?: number | null
  supply_price?: number | null
  active?: boolean | null
  is_active?: boolean | null
  deleted_at?: string | null
  has_variants?: boolean | null
  has_inventory?: boolean | null
  variant_parent_id?: string | null
  variant_options?: unknown
  last_sale_date_local?: string | null
  last_received_date?: string | null
  created_at?: string | null
  updated_at?: string | null
  image_thumbnail_url?: string | null
  image_url?: string | null
  total_stock: number
  lightspeed_product_link?: string | null
  score?: number | null
  // Tag-backed lifecycle fields (backend enrich_group_members) - drive the
  // Compare Duplicates dialog's Issues chips + Ignore actions. Entries
  // match IssuesChipEntry; typed loosely to keep services/ free of
  // config/ imports.
  tag_id?: number | null
  tag_status?: string | null
  errors?: any[]
  recently_closed_errors?: any[]
}

export interface DuplicateGroupDetailResponse {
  fp_code: string
  group_id: string
  band?: string | null
  top_score?: number | null
  match_reasons: string[]
  members: DuplicateGroupMember[]
  /** exception_types.action_policy verdict - false hides Ignore actions. */
  allow_ignore?: boolean
}

export interface DuplicateSaleGroupMember {
  ls_id: string
  invoice_number?: string | null
  sale_date_local?: string | null
  /** Full LS POS-side sale timestamp (from ``ls_sales.sale_date``).
   *  This is the real transaction time and is what the detector uses
   *  for closeness scoring — NOT our local sync batch time. */
  sale_date?: string | null
  status?: string | null
  total_amount_incl_tax: number
  total_tax_amount?: number | null
  customer_id?: string | null
  customer_first_name?: string | null
  customer_last_name?: string | null
  customer_company?: string | null
  outlet_id?: string | null
  outlet_name?: string | null
  register_id?: string | null
  return_for?: string | null
  line_count?: number | null
  line_signature?: string | null
  lightspeed_link?: string | null
  xero_link?: string | null
  xero_invoice_id?: string | null
  score?: number | null
  // Tag-backed lifecycle fields (backend enrich_group_members). tag_status,
  // not status - `status` above is the LS sale status. Entries match
  // IssuesChipEntry; typed loosely to keep services/ free of config/ imports.
  tag_id?: number | null
  tag_status?: string | null
  errors?: any[]
  recently_closed_errors?: any[]
}

export interface DuplicateSaleGroupDetailResponse {
  fp_code: string
  group_id: string
  band?: string | null
  top_score?: number | null
  match_reasons: string[]
  members: DuplicateSaleGroupMember[]
  /** exception_types.action_policy verdict - false hides Ignore actions. */
  allow_ignore?: boolean
}

const inspectorApi = new InspectorApiService()
export default inspectorApi